import { getAccount } from '@common/login/selectors';
import { getTableViewType, setTableViewType } from '@features/ui/reducer';
import SegmentFilter from '@features/ui/SegmentFilter';
import { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/components/table/TableViewToggles';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { generatePushToDataLayer } from '@utils/tracking';
import { camelCase } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../setup/store';

export const SettingsToolbar: React.FunctionComponent<{
    disabled?: boolean;
    toggleSettings: (visible: boolean) => void;
    location?: string;
    // eslint-disable-next-line react/prop-types
}> = ({ disabled, toggleSettings, location }) => {
    const dispatch = useAppDispatch();
    const viewType = useSelector(getTableViewType);
    const account: string | undefined = useSelector(getAccount);
    const pushToDataLayer = generatePushToDataLayer(account);
    const tableViewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const buttons = tableViewRef.current?.querySelectorAll<HTMLButtonElement>(
            '.TableViewToggles button',
        );

        const views = ['table', 'list', 'split'];

        buttons?.forEach((button, index) => {
            const view = views[index];

            if (!view) return;

            button.setAttribute('data-test', `table-view-${view}`);
            button.setAttribute('data-track-ga-event-trigger', 'click');
            button.setAttribute('data-track-ga-event-name', 'perform_table_view_changed');
            button.setAttribute('data-track-ga-event-element-name', view);
        });
    }, []);

    const onViewTypeChange = (viewType: string) => {
        pushToDataLayer({
            eventTrigger: 'click',
            eventCategory: 'perform, tableSettings',
            eventAction: 'toggleTableView',
            eventLabel: `${camelCase(viewType)}View, ${location}`,
        });

        dispatch(setTableViewType(viewType as TableViewTogglesViewType));
    };

    return (
        <TableToolbar>
            <div className="table-toolbar-container">
                <div className="table-toolbar-group-left">
                    <div className="table-toolbar-column min-width-200">
                        <SegmentFilter />
                    </div>
                </div>

                <div className="table-toolbar-group-right display-none-xs display-flex-sm">
                    <div className="table-toolbar-column table-toolbar-column-spacer">
                        <div ref={tableViewRef}>
                            <TableViewToggles
                                initialViewType={viewType}
                                viewType={viewType}
                                onViewTypeChange={onViewTypeChange}
                            />
                        </div>
                    </div>
                    <div className="table-toolbar-column">
                        <div className="btn-toolbar">
                            <button
                                disabled={disabled}
                                className="btn btn-default btn-icon-only"
                                data-test="table-settings-toggler"
                                data-track-ga-event-trigger="click"
                                data-track-ga-event-name="perform_table_settings_opened"
                                onClick={() => toggleSettings(true)}
                            >
                                <span className="rioglyph rioglyph-cog" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </TableToolbar>
    );
};

export default SettingsToolbar;
